import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { createSpot } from "@/lib/camping.functions";
import { supabase } from "@/integrations/supabase/client";
import { ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/campingtips/ny")({
  head: () => ({ meta: [{ title: "Lägg upp campingtips" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: NewSpotPage,
});

const REGIONS = ["Norrbotten", "Västerbotten", "Jämtland", "Ångermanland", "Medelpad", "Hälsingland", "Dalarna", "Gästrikland", "Uppland", "Södermanland", "Västmanland", "Värmland", "Närke", "Östergötland", "Västergötland", "Bohuslän", "Halland", "Småland", "Öland", "Gotland", "Blekinge", "Skåne", "Övriga Europa"];
const TYPES = ["Camping", "Ställplats", "Naturplats", "Vandrarhem", "Marina"];
const SERVICE_OPTIONS = [
  ["has_electricity", "El"], ["has_fresh_water", "Färskvatten"], ["has_grey_water", "Gråvattentömning"],
  ["has_latrine", "Latrintömning"], ["has_toilet", "Toalett"], ["has_shower", "Dusch"], ["has_waste_bins", "Sopor"],
] as const;

function NewSpotPage() {
  const navigate = useNavigate();
  const create = useServerFn(createSpot);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [region, setRegion] = useState("");
  const [spotType, setSpotType] = useState("Camping");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [facts, setFacts] = useState<Record<string, string>>({});
  const [services, setServices] = useState<Record<string, boolean>>({});
  const [sourceUrls, setSourceUrls] = useState("");
  const [images, setImages] = useState<{ path: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) throw new Error("Ej inloggad");
      const uploaded: { path: string; url: string }[] = [];
      for (const file of files.slice(0, 10 - images.length)) {
        if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name}: max 8 MB`); continue; }
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `${uid}/spots/${Date.now()}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
        const { error } = await supabase.storage.from("user-content").upload(path, file, { contentType: file.type });
        if (error) { toast.error(error.message); continue; }
        const { data: signed } = await supabase.storage.from("user-content").createSignedUrl(path, 60 * 60);
        uploaded.push({ path, url: signed?.signedUrl ?? "" });
      }
      setImages((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  function removeImage(idx: number) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (title.trim().length < 3) { toast.error("Titel för kort"); return; }
    if (description.trim().length < 20) { toast.error("Beskriv platsen mer utförligt (minst 20 tecken)"); return; }
    setSaving(true);
    try {
      const { slug } = await create({
        data: {
          title: title.trim(),
          description: description.trim(),
          region: region || undefined,
          spot_type: spotType || undefined,
          latitude: lat ? Number(lat) : null,
          longitude: lng ? Number(lng) : null,
          image_urls: images.map((i) => i.path),
          price_info: facts.price_info,
          season_info: facts.season_info,
          booking_info: facts.booking_info,
          stay_length_info: facts.stay_length_info,
          has_electricity: services.has_electricity ?? null,
          has_fresh_water: services.has_fresh_water ?? null,
          has_grey_water: services.has_grey_water ?? null,
          has_latrine: services.has_latrine ?? null,
          has_toilet: services.has_toilet ?? null,
          has_shower: services.has_shower ?? null,
          has_waste_bins: services.has_waste_bins ?? null,
          surface: facts.surface,
          level_info: facts.level_info,
          max_vehicle_length_m: facts.max_vehicle_length_m ? Number(facts.max_vehicle_length_m) : null,
          access_info: facts.access_info,
          noise_info: facts.noise_info,
          mobile_coverage: facts.mobile_coverage,
          pets_info: facts.pets_info,
          children_info: facts.children_info,
          accessibility_info: facts.accessibility_info,
          nearest_services: facts.nearest_services,
          best_for: facts.best_for,
          not_suitable_for: facts.not_suitable_for,
          backup_plan: facts.backup_plan,
          visited_at: facts.visited_at || null,
          fact_status: "experience",
          source_urls: sourceUrls.split(/\n|,/).map((url) => url.trim()).filter(Boolean),
        },
      });
      toast.success("Tips publicerat!");
      navigate({ to: "/campingtips/$slug", params: { slug } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte publicera");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-warm">
      <div className="container-edge max-w-3xl py-16 md:py-20">
        <p className="eyebrow">Community · Nytt tips</p>
        <h1 className="mt-3 font-display text-4xl md:text-6xl">
          Dela en <span className="font-italic-display text-accent">favoritplats</span>.
        </h1>

        <form onSubmit={submit} className="mt-10 space-y-5 rounded-3xl border border-border bg-card p-6 md:p-8 elev-shadow">
          <div>
            <label className="mb-2 block text-sm font-medium">Titel *</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
              placeholder="t.ex. Undersåkers Camping"
              className="h-12 rounded-2xl px-4" />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium">Region</label>
              <select value={region} onChange={(e) => setRegion(e.target.value)}
                className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20">
                <option value="">Välj…</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Typ av plats</label>
              <select value={spotType} onChange={(e) => setSpotType(e.target.value)}
                className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Beskrivning *</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={8} maxLength={6000}
              placeholder="Berätta om platsen — utsikt, faciliteter, pris, tips på restaurang eller aktiviteter i närheten…"
              className="rounded-2xl px-4 py-3" />
          </div>

          <fieldset className="space-y-5 border-t border-border pt-6">
            <legend className="font-display text-2xl">Praktiskt för husbilen</legend>
            <p className="text-sm text-muted-foreground">Fyll bara i sådant du själv sett eller har en källa till. Tomma uppgifter visas inte.</p>
            <div className="grid gap-5 sm:grid-cols-2">
              {[
                ["price_info", "Pris och vad som ingår", "t.ex. 250 kr/natt, el tillkommer"],
                ["season_info", "Säsong", "t.ex. maj–september"],
                ["booking_info", "Bokning", "drop-in, app, telefon eller förbokning"],
                ["stay_length_info", "Lagom vistelse", "t.ex. 1 natt eller 2–3 dagar"],
                ["surface", "Underlag", "t.ex. plant grus, gräs eller asfalt"],
                ["level_info", "Lutning", "plant, svag lutning eller klossar behövs"],
                ["max_vehicle_length_m", "Största fordonslängd du känner till", "meter"],
                ["mobile_coverage", "Mobiltäckning", "operatör och hur den fungerade"],
              ].map(([key, label, placeholder]) => (
                <div key={key}>
                  <Label htmlFor={key} className="mb-2 block">{label}</Label>
                  <Input id={key} type={key === "max_vehicle_length_m" ? "number" : "text"} min={key === "max_vehicle_length_m" ? 2 : undefined} max={key === "max_vehicle_length_m" ? 30 : undefined} step={key === "max_vehicle_length_m" ? 0.1 : undefined} value={facts[key] ?? ""} onChange={(e) => setFacts((current) => ({ ...current, [key]: e.target.value }))} placeholder={placeholder} className="h-12 rounded-2xl px-4" />
                </div>
              ))}
            </div>
            <div>
              <p className="mb-3 text-sm font-medium">Service du vet finns</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {SERVICE_OPTIONS.map(([key, label]) => (
                  <Label key={key} htmlFor={key} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 font-normal">
                    <Checkbox id={key} checked={services[key] ?? false} onCheckedChange={(checked) => setServices((current) => ({ ...current, [key]: checked === true }))} />
                    {label}
                  </Label>
                ))}
              </div>
            </div>
            {[
              ["access_info", "Infart och vändmöjlighet", "Beskriv trånga passager, backning, höjd eller viktbegränsning."],
              ["noise_info", "Nattro och buller", "Väg, järnväg, hamn, evenemang eller lugnt nattetid."],
              ["nearest_services", "Närmaste mat, drivmedel och service", "Vad finns nära och ungefär hur långt bort?"],
              ["best_for", "Platsen passar bäst för", "Till exempel barnfamilj, genomresa eller vandring."],
              ["not_suitable_for", "Platsen passar sämre för", "Till exempel stora ekipage eller den som behöver full service."],
              ["pets_info", "Hund", "Tillåten, koppeltvång, badmöjlighet eller annat viktigt."],
              ["children_info", "Barn", "Lekplats, bad, trafik eller andra förutsättningar."],
              ["accessibility_info", "Tillgänglighet", "Underlag, nivåskillnader och tillgänglig toalett."],
              ["backup_plan", "Reservplan", "Ett närliggande alternativ om platsen är full eller stängd."],
            ].map(([key, label, placeholder]) => (
              <div key={key}>
                <Label htmlFor={key} className="mb-2 block">{label}</Label>
                <Textarea id={key} value={facts[key] ?? ""} onChange={(e) => setFacts((current) => ({ ...current, [key]: e.target.value }))} placeholder={placeholder} className="min-h-20 rounded-2xl px-4 py-3" />
              </div>
            ))}
          </fieldset>

          <fieldset className="space-y-5 border-t border-border pt-6">
            <legend className="font-display text-2xl">Besök och källor</legend>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="visited_at" className="mb-2 block">När besökte du platsen?</Label><Input id="visited_at" type="date" value={facts.visited_at ?? ""} onChange={(e) => setFacts((current) => ({ ...current, visited_at: e.target.value }))} className="h-12 rounded-2xl px-4" /></div>
              <div><Label htmlFor="sources" className="mb-2 block">Källor (valfritt)</Label><Textarea id="sources" value={sourceUrls} onChange={(e) => setSourceUrls(e.target.value)} placeholder="En webbadress per rad" className="min-h-20 rounded-2xl px-4 py-3" /></div>
            </div>
          </fieldset>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium">Latitud (valfritt)</label>
              <input value={lat} onChange={(e) => setLat(e.target.value)} type="number" step="0.000001" placeholder="63.331"
                className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Longitud (valfritt)</label>
              <input value={lng} onChange={(e) => setLng(e.target.value)} type="number" step="0.000001" placeholder="13.256"
                className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Bilder (upp till 10)</label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-square overflow-hidden rounded-2xl border border-border">
                  <img src={img.url} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)}
                    className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-background/90 text-foreground shadow">
                    <X size={13} />
                  </button>
                </div>
              ))}
              {images.length < 10 && (
                <label className="grid aspect-square cursor-pointer place-items-center rounded-2xl border-2 border-dashed border-border text-muted-foreground hover:border-accent hover:text-accent">
                  <div className="flex flex-col items-center gap-1 text-xs">
                    <ImagePlus size={22} />
                    {uploading ? "Laddar…" : "Lägg till"}
                  </div>
                  <input type="file" multiple accept="image/*" onChange={onFiles} className="hidden" />
                </label>
              )}
            </div>
          </div>

          <Button disabled={saving || uploading} size="lg" className="rounded-full">
            {saving ? "Publicerar…" : "Publicera tips"}
          </Button>
        </form>
      </div>
    </section>
  );
}
